import React, { useEffect, useRef, useState, Suspense } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Observer } from "gsap/Observer";
import Hero from "../components/Hero";
import ScrollStackSection from "../components/ScrollStackSection";
import TrendingSection from "../components/TrendingSection";
import SlidingText from "../components/SlidingText";
import CharFadeIn from "../components/CharFadeIn";
import ScrollRotateModel from "../components/ScrollRotateModel";

gsap.registerPlugin(ScrollTrigger, Observer);

// Data for injection site cards
const injectionCards = [
    {
        id: 1,
        title: "Choose Injection Site",
        description:
            "You can inject Mounjaro in your stomach (abdomen), thigh, or upper arm. If you want to use your upper arm, ask someone else to help you with the injection.",
        image: "/images/benifits/placeholder-1.png", // Keeping placeholder as requested
        imageAlt: "Injection Site Selection",
        backgroundColor: "#252C34",
    },
    {
        id: 2,
        title: "Prepare the Pen",
        description:
            "Wash your hands. Pull the gray base cap straight off. Do not touch the needle. Make sure the pen is locked until you are ready to inject.",
        image: "/images/benifits/placeholder-2.png",
        imageAlt: "Pen Preparation",
        backgroundColor: "#323B46",
    },
    {
        id: 3,
        title: "Injecting Mounjaro",
        description:
            "Place the clear base flat against your skin at the injection site. Unlock the pen. Press and hold the purple injection button for up to 10 seconds. Listen for the click.",
        image: "/images/benifits/placeholder-3.png",
        imageAlt: "Injection Process",
        backgroundColor: "#64768C",
    },
    {
        id: 4,
        title: "Weekly Rotation",
        description:
            "Rotate your injection site with each weekly dose. You may inject in the same area, but try not to inject in the exact same spot as the week before.",
        image: "/images/benifits/placeholder-4.png",
        imageAlt: "Site Rotation",
        backgroundColor: "#A4B4C7",
    },
];

function Home() {
    const heroRef = useRef(null);
    const modelContainerRef = useRef(null);
    const [scrollLocked, setScrollLocked] = useState(true);
    const sectionRefs = useRef([]);

    useEffect(() => {
        if (!heroRef.current) return;

        const st = ScrollTrigger.create({
            trigger: heroRef.current,
            start: "top top",
            end: "+=10000",
            pin: true,
            pinSpacing: true,
        });

        const holdMs = 2000;
        const timer = window.setTimeout(() => {
            setScrollLocked(false);
            st.kill();
        }, holdMs);

        return () => {
            window.clearTimeout(timer);
            st && st.kill();
        };
    }, []);

    useEffect(() => {
        if (!scrollLocked) return;

        document.body.style.overflow = "hidden";

        const disableScroll = (e) => e.preventDefault();
        window.addEventListener("wheel", disableScroll, { passive: false });
        window.addEventListener("touchmove", disableScroll, { passive: false });

        const timeout = setTimeout(() => {
            document.body.style.overflow = "";
            setScrollLocked(false);
        }, 2000);

        return () => {
            clearTimeout(timeout);
            window.removeEventListener("wheel", disableScroll);
            window.removeEventListener("touchmove", disableScroll);
            document.body.style.overflow = "";
        };
    }, [scrollLocked]);

    return (
        <div className="relative">
            {/* Hero Section with Pinned Animation */}
            <section ref={heroRef} className="h-screen w-full">
                <Hero scrollLocked={scrollLocked} />
            </section>

            {/* Incretins Information Section */}
            <section className="h-auto pt-40 pb-72 w-full bg-gradient-to-r from-[#BABABA] via-[#BABABA] to-[#B9B9B9] flex items-center justify-center overflow-hidden section-stack">
                <div className="content-animation relative z-10 flex flex-col md:flex-row items-center justify-between w-full mx-auto px-6 md:px-20 py-6 md:py-0">
                    <div className="flex-1 md:basis-1/3 flex flex-col items-start justify-center md:px-10">
                        <CharFadeIn
                            text={
                                "Experience the first and only GIP and GLP-1 receptor agonist. Mounjaro works with your body to control appetite and improve how you process sugar."
                            }
                            className="font-bold text-[32px] md:text-[70px] mb-4 md:mb-8 text-white text-left md:text-center"
                        />
                    </div>
                </div>
            </section>

            {/* Incretins Functions List Section */}
            <section className="h-auto py-12 md:h-screen w-full bg-black flex items-center justify-center overflow-hidden section-stack">
                <div className="content-animation flex flex-col md:flex-row items-start w-full px-6 md:px-20 py-6 md:py-0">
                    <div className="w-full md:flex-1 flex items-center mb-6 md:mb-0">
                        <CharFadeIn
                            text={
                                "Harnessing the power of your body's natural signals.\nMounjaro affects the body in three key ways:"
                            }
                            className="font-medium text-[28px] md:text-[44px] text-left leading-tight text-white"
                            data-delay="0.05"
                            data-stagger-each="0.02"
                            data-stagger-amount="1.0"
                        />
                    </div>
                    <div className="w-full md:flex-1 flex flex-col justify-center items-start md:pl-20">
                        <div className="flex flex-col gap-6 md:gap-16 relative w-full">
                            <div className="absolute left-5 md:left-6 top-10 bottom-10 w-[4px] bg-white z-0" />
                            {[
                                "Slowing down the emptying of the stomach",
                                "Sending signals to your brain that you're full after eating",
                                "Increasing insulin levels",
                            ].map((text, idx) => (
                                <div
                                    key={idx}
                                    className="flex items-start gap-4 md:gap-10 relative z-10"
                                >
                                    <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                                        <img
                                            src="/images/home/Relume.svg"
                                            alt="star"
                                            className="w-6 h-6 md:w-8 md:h-8"
                                        />
                                    </div>
                                    <CharFadeIn
                                        text={text}
                                        className="flex-1 text-white text-left text-[20px] md:text-[36px] font-medium"
                                        data-delay="0.05"
                                        data-stagger-each="0.02"
                                        data-stagger-amount="1.2"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Container for 3D Model Across Three Sections */}
            <div ref={modelContainerRef} className="relative">
                <Suspense fallback={null}>
                    {modelContainerRef.current && (
                        <ScrollRotateModel sectionRefs={sectionRefs} />
                    )}
                </Suspense>

                {/* Eligibility Criteria Section */}
                <section className="min-h-screen w-full bg-gray-100 flex items-center justify-center overflow-hidden relative py-12 md:py-0" ref={el => sectionRefs.current[0] = el}>
                    <div className="absolute inset-0 flex items-center justify-center z-0 pointer-events-none select-none">
                        <SlidingText
                            text="Mounjaro"
                            className="font-medium text-center text-[120px] md:text-[280px] text-black"
                        />
                    </div>
                    <div className="absolute top-8 md:top-12 right-6 md:right-12 max-w-[340px] md:max-w-2xl z-0 text-left">
                        <CharFadeIn
                            text="a BMI (body mass index) of 30 kg/m or over (obese)"
                            className="text-black text-lg md:text-[24px] leading-tight"
                        />
                    </div>
                    <div className="absolute bottom-8 md:bottom-12 left-6 md:left-12 max-w-[340px] md:max-w-xl z-0 text-left">
                        <CharFadeIn
                            text="a BMI between 27 and 30 kg/m² (overweight), and a weight-related health condition such as high cholesterol, high blood pressure,"
                            className="text-black text-lg md:text-[24px] leading-tight"
                        />
                    </div>
                </section>

                {/* How Mounjaro Works Section 1 */}
                <section className="min-h-screen w-full bg-black flex items-center justify-center overflow-hidden relative py-12 md:py-0" ref={el => sectionRefs.current[1] = el}>
                    <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none select-none">
                        <SlidingText
                            text="Mounjaro"
                            className="font-medium text-center text-[120px] md:text-[280px] text-white opacity-80"
                        />
                    </div>
                    <div className="absolute top-8 md:top-12 right-6 md:right-12 max-w-[340px] md:max-w-2xl z-20 text-left">
                        <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
                            <span className="text-white text-xl md:text-2xl">✱</span>
                            <span className="text-white text-xl md:text-2xl font-bold">
                How does Mounjaro work
              </span>
                        </div>
                        <p className="text-white text-sm md:text-base leading-relaxed">
                            The active ingredient tirzepatide works by activating the receptors
                            of 2 natural incretin hormones: GIP and GLP-1. Both of these
                            hormones affect your appetite by slowing down the emptying of the
                            stomach after a meal. They also send signals to your brain to reduce
                            your appetite after eating. This action creates a feeling of being
                            full and makes you feel less hungry, even when you have eaten less,
                            which can aid in weight loss.
                        </p>
                    </div>
                    <div className="absolute bottom-8 md:bottom-12 left-6 md:left-12 max-w-[340px] md:max-w-xl z-20 text-left">
                        <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
                            <span className="text-white text-xl md:text-2xl">✱</span>
                            <span className="text-white text-xl md:text-2xl font-bold">
                How effective is Mounjaro
              </span>
                        </div>
                        <p className="text-white text-sm md:text-base leading-relaxed">
                            Mounjaro is an effective treatment for both weight loss and
                            regulating blood sugar levels. A recent clinical trial has shown
                            that people who used Mounjaro lost up to 22.5% of their body weight
                            on the highest maintenance dose (15mg tirzepatide, once per week)
                            within 72 weeks.
                        </p>
                    </div>
                </section>

                <section className="min-h-screen w-full bg-white flex items-center justify-center overflow-hidden relative py-12 md:py-0" ref={el => sectionRefs.current[2] = el}>
                    <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none select-none">
                        <SlidingText
                            text="Results"
                            className="font-medium text-center text-[120px] md:text-[280px] text-black"
                        />
                    </div>
                    <div className="absolute top-8 md:top-12 right-6 md:right-12 max-w-[340px] md:max-w-2xl z-20 text-left">
                        <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
                            <span className="text-black text-xl md:text-2xl">✱</span>
                            <span className="text-black text-xl md:text-2xl font-bold">
                Proven Clinical Results
              </span>
                        </div>
                        <p className="text-black text-sm md:text-base leading-relaxed">
                            In a 72-week clinical trial, participants taking Mounjaro 15 mg lost an average of 22.5% of their body weight. That's about 52 lbs for someone starting at 230 lbs. Mounjaro helps you lose weight and keep it off by addressing the underlying biological drivers of obesity.
                        </p>
                    </div>
                    <div className="absolute bottom-8 md:bottom-12 left-6 md:left-12 max-w-[340px] md:max-w-xl z-20 text-left">
                        <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
                            <span className="text-black text-xl md:text-2xl">✱</span>
                            <span className="text-black text-xl md:text-2xl font-bold">
                Sustained Weight Loss
              </span>
                        </div>
                        <p className="text-black text-sm md:text-base leading-relaxed">
                             Participants also saw significant reductions in waist circumference and improvements in other cardiometabolic markers. Mounjaro isn't just about the number on the scale; it's about improving your overall metabolic health for the long term.
                        </p>
                    </div>
                </section>
            </div>

            {/* Injection Sites Section */}
            <ScrollStackSection cards={injectionCards} />

            {/* Trending Information Section */}
            <TrendingSection />
        </div>
    );
}

export default Home;